import React from 'react';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useVspeedModalLogic } from 'ff/debrief/components/modals/VspeedModal/useVspeedModalLogic';
import { DebriefAircraft } from 'ff/debrief/models';
import DebriefDialogTitle from 'ff/debrief/components/modals/DebriefDialogTitle';
import VspeedOverrideForm from 'ff/debrief/components/modals/VspeedModal/VspeedOverrideForm';
import { isCustom } from 'ff/debrief/utils';

export const VspeedModal = () => {
    const { isOpen, close, handleSubmit, initialValues } =
        useVspeedModalLogic();

    const baseSchema = Yup.number()
        .typeError('Must be a number')
        .min(0, 'Must be greater than zero')
        .integer('Must be an integer')
        .when('aircraftModel', {
            is: (aircraftModel: DebriefAircraft) =>
                isCustom(aircraftModel?.model),
            then: schema => schema.required(),
            otherwise: schema => schema.optional().nullable(),
        });

    const fieldSchema = baseSchema.when('preferredSpeedUnit', {
        is: 'mph',
        then: schema => schema.max(1200, 'Must be less than 1380 mph'),
        otherwise: schema => schema.max(1200, 'Must be less than 1200 kts'),
    });

    const vmxtSchema = baseSchema.when('preferredSpeedUnit', {
        is: 'mph',
        then: schema => schema.max(30, 'Maximum of 35 mph'),
        otherwise: schema => schema.max(30, 'Maximum of 30 kts'),
    });
    const aircraftSchema = Yup.mixed().required(
        'Please enter an aircraft model'
    );

    const formSchema = Yup.object().shape({
        vr: fieldSchema,
        vx: fieldSchema,
        vy: fieldSchema,
        vref: fieldSchema,
        vne: fieldSchema,
        vs0: fieldSchema,
        vs1: fieldSchema,
        vsw: fieldSchema,
        vmxt: vmxtSchema,
        tailNumber: Yup.object().required('Please enter a tail number'),
        aircraftModel: Yup.object()
            .shape({
                manufacturer: aircraftSchema,
                model: aircraftSchema,
            })
            .required('Please enter an aircraft model'),
    });

    return (
        <Dialog open={isOpen} onClose={close} maxWidth="sm" fullWidth>
            <DebriefDialogTitle onClose={close}>V-Speeds</DebriefDialogTitle>
            <DialogContent>
                <Typography mb={2}>
                    Based on the detected tail number, we&apos;ve identified
                    this aircraft. If the tail number or model is incorrect, you
                    can edit/select the correct ones using the options below.
                    This change will only impact the Debrief view.
                </Typography>
                <Formik
                    enableReinitialize={true}
                    initialValues={initialValues}
                    validationSchema={formSchema}
                    onSubmit={handleSubmit}
                    validateOnMount={true}
                >
                    <Form>
                        <VspeedOverrideForm />
                    </Form>
                </Formik>
            </DialogContent>
        </Dialog>
    );
};
